// Package Imports
import { differenceInPercentage } from '@/lib/functions/differenceInPercentage';
import { isDateInSpecifiedRange } from '@/lib/functions/isDateInSpecifiedRange';

/**
 * Determines the best valid case-level price (e.g., 6-pack or 12-pack).
 *
 * Rules:
 * - Case pricing must save at least 2 % over single-unit purchases.
 * - Discounts (promo or key club) must be > 0 and lower than the full case total.
 * - Discounts must be inside their active date windows.
 * - Key-Club discounts outrank promos.
 * - Always includes the raw key-club discount in the return object (for UI display).
 */
export const calculateCasePrice = ({
  price,
  casePrice,
  caseQuantity,
  promoCasePrice,
  promoPriceStartDate,
  promoPriceEndDate,
  keyClubCaseDiscount,
  keyClubDiscount,
  keyClubDiscountStartDate,
  keyClubDiscountEndDate,
}: {
  price?: number | null;
  casePrice?: number | null;
  caseQuantity?: number | null;
  promoCasePrice?: number | null;
  promoPriceStartDate?: string | null;
  promoPriceEndDate?: string | null;
  keyClubCaseDiscount?: number | null;
  keyClubDiscount?: number | null; 
  keyClubDiscountStartDate?: string | null;
  keyClubDiscountEndDate?: string | null;
}): {
  casePrice: number | null;
  casePricePromoDiscount: number | null;
  casePriceKeyclubDiscount: number | null;
  caseQuantity: number | null;
} => {
  const now = new Date();

  // Bail if core inputs are missing
  if (price == null || casePrice == null || caseQuantity == null || caseQuantity <= 0) {
    return {
      casePrice: null,
      caseQuantity: null,
      casePricePromoDiscount: null,
      casePriceKeyclubDiscount: null,
    };
  }

  const fullCaseTotal = price * caseQuantity;

  // Validate base case price (must beat single-unit by ≥ 2 %)
  const baseCaseSavings = differenceInPercentage(fullCaseTotal, casePrice);
  const baseCasePrice = baseCaseSavings >= 0.02 ? casePrice : null;

  // Use explicit case-discount if present; otherwise fall back to per-unit * qty
  const resolvedCasePriceKeyClubDiscount =
    keyClubCaseDiscount != null && keyClubCaseDiscount > 0
      ? keyClubCaseDiscount
      : keyClubDiscount != null && keyClubDiscount > 0 && caseQuantity > 0
        ? keyClubDiscount * caseQuantity
        : null;

  // Evaluate key-club discount (if any)
  const keyClubValid =
    resolvedCasePriceKeyClubDiscount != null &&
    resolvedCasePriceKeyClubDiscount > 0 &&
    isDateInSpecifiedRange(now, keyClubDiscountStartDate, keyClubDiscountEndDate);

  // Promo applies if not null, > 0, and within active date range
  const promoValid =
    promoCasePrice != null &&
    promoCasePrice > 0 &&
    isDateInSpecifiedRange(now, promoPriceStartDate, promoPriceEndDate);

  // Return the case pricing details
  return {
    casePrice: baseCasePrice,
    caseQuantity: baseCasePrice ? caseQuantity : null,
    casePricePromoDiscount: promoValid ? promoCasePrice : null,
    casePriceKeyclubDiscount: keyClubValid ? resolvedCasePriceKeyClubDiscount : null,
  };
};
